/**
 * Reusable Table UI Component
 */

import type { ReactNode } from "react";

export interface TableProps {
  children: ReactNode;
  className?: string;
}

export const Table = ({ children, className }: TableProps) => {
  return (
    <div className={`w-full ${className || ""} overflow-x-auto`}>
      <table className="w-full border-separate border-spacing-y-2">{children}</table>
    </div>
  );
};

export const TableHeader = ({ children }: { children: ReactNode }) => {
  return <thead className="bg-slate-900">{children}</thead>;
};

export const TableBody = ({ children }: { children: ReactNode }) => {
  return <tbody className="bg-transparent">{children}</tbody>;
};

export const TableRow = ({ children }: { children: ReactNode }) => {
  return (
    <tr className="bg-[#f9f9fa] even:bg-[#f1f1f4] hover:bg-slate-100 h-12 transition-colors duration-200">
      {children}
    </tr>
  );
};

export const TableHead = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <th
      className={`text-left text-white py-3.5 px-4 bg-slate-900 font-bold text-xs leading-4 tracking-[0.005em] first:rounded-l-lg first:rounded-bl-lg last:rounded-r-lg last:rounded-br-lg last:text-right ${className || ""}`}
    >
      {children}
    </th>
  );
};

export interface TableCellProps {
  children: ReactNode;
  className?: string;
  colSpan?: number;
}

export const TableCell = ({ children, className, colSpan }: TableCellProps) => {
  return (
    <td
      colSpan={colSpan}
      className={`py-3.5 px-4 text-slate-900 bg-inherit text-left font-semibold text-sm leading-5 tracking-[0.005em] first:rounded-l-lg first:rounded-bl-lg last:rounded-r-lg last:rounded-br-lg last:text-right ${className || ""}`}
    >
      {children}
    </td>
  );
};

export interface TableLoadingRowProps {
  /** Number of columns to span */
  colSpan: number;
  /** Custom loading message */
  message?: string;
}

/**
 * Reusable table loading row with spinner animation.
 * Displays a centered loading indicator spanning the full table width.
 */
export const TableLoadingRow = ({
  colSpan,
  message = "Loading...",
}: TableLoadingRowProps) => {
  return (
    <tr className="bg-[#f9f9fa]">
      <td
        colSpan={colSpan}
        className="py-12 px-4 text-center rounded-lg"
      >
        <div className="flex flex-col items-center justify-center gap-3">
          <div
            className="w-8 h-8 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin"
            role="status"
            aria-label="Loading"
          />
          <span className="text-sm text-slate-500 font-medium">{message}</span>
        </div>
      </td>
    </tr>
  );
};

export interface TableEmptyRowProps {
  /** Number of columns to span */
  colSpan: number;
  /** Custom empty state message */
  message?: string;
}

/**
 * Reusable table empty state row.
 * Displays a centered message when the table has no data.
 */
export const TableEmptyRow = ({
  colSpan,
  message = "No data found.",
}: TableEmptyRowProps) => {
  return (
    <tr className="bg-[#f9f9fa]">
      <td
        colSpan={colSpan}
        className="py-12 px-4 text-center text-slate-400 rounded-lg"
      >
        {message}
      </td>
    </tr>
  );
};

export { Pagination } from "./pagination";
export type { PaginationProps } from "./pagination";
